import PropTypes from 'prop-types';
import { IconButton, InputBase, Paper, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSearchParams } from 'react-router-dom';

/**
 * Standardised search field used across list pages.
 *
 * Replaces the repeated pattern of:
 *   <Paper><InputBase startAdornment={Refresh} endAdornment={Search} ... /></Paper>
 *
 * Behaviour preserved exactly:
 *   - Refresh button → clears value + url param + triggers reload with empty string
 *   - Enter key OR Search button → triggers onSearch(currentValue)
 *   - disabled on all controls
 *   - responsive width (180 on mobile, 400 desktop) unless overridden
 *
 * Optional searchParamKey: when provided, the component reads/writes the URL ?key= param,
 * keeping the existing url-state pattern intact without consumers having to wire it.
 */
const SearchField = ({
    value,
    onChange,
    onSearch,
    onReset,
    placeholder = 'Search',
    disabled = false,
    searchParamKey,
    elevation = 2,
    width,
    sx = {}
}) => {
    const theme = useTheme();
    const matchDownSM = useMediaQuery(theme.breakpoints.down('md'));
    const [searchParams, setSearchParams] = useSearchParams();

    const writeParam = (v) => {
        if (!searchParamKey) return;
        searchParams.set(searchParamKey, v);
        setSearchParams(searchParams);
    };

    const handleChange = (e) => {
        const v = e.target.value;
        onChange?.(v);
        writeParam(v);
    };

    const handleSearch = () => {
        onSearch?.(value);
    };

    const handleReset = () => {
        onChange?.('');
        writeParam('');
        if (onReset) onReset();
        else onSearch?.('');
    };

    const handleKeyDown = (e) => {
        if (e.keyCode === 13 || e.key === 'Enter') {
            e.preventDefault();
            handleSearch();
        }
    };

    return (
        <Paper
            elevation={elevation}
            style={{
                display: 'flex',
                width: width ?? (matchDownSM ? 180 : 400)
            }}
            sx={sx}
        >
            <InputBase
                type="text"
                fullWidth
                disabled={disabled}
                style={{ marginLeft: 1, flex: 1, fontFamily: 'boonhome-400' }}
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                inputProps={{ 'aria-label': placeholder }}
                startAdornment={
                    <IconButton disabled={disabled} aria-label="refresh" onClick={handleReset}>
                        <RefreshIcon />
                    </IconButton>
                }
                endAdornment={
                    <IconButton disabled={disabled} aria-label="search" onClick={handleSearch}>
                        <SearchIcon />
                    </IconButton>
                }
            />
        </Paper>
    );
};

SearchField.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func,
    onSearch: PropTypes.func,
    onReset: PropTypes.func,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    searchParamKey: PropTypes.string,
    elevation: PropTypes.number,
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    sx: PropTypes.object
};

export default SearchField;
